import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Button, Badge, Empty, Spin } from 'antd';
import { Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import { notificationService } from '@/services/notificationService';
import { PageHeader, Card } from '@/components/common/PageHeader';

dayjs.extend(relativeTime);
dayjs.locale('vi');

const TYPE_ICONS: Record<string, string> = {
  TASK_ASSIGNED: '📝',
  SIGN_REQUEST: '🔐',
  TASK_DUE: '⏰',
  TASK_OVERDUE: '🚨',
  MENTION: '💬',
  TASK_UPDATE: '✅',
  REPORT_READY: '📊',
};

export function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const qc = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => notificationService.list(filter),
    refetchInterval: 30 * 1000, // refresh mỗi 30s
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: notificationService.markAllAsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const handleClick = (n: any) => {
    if (!n.isRead) markReadMutation.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <>
      <PageHeader
        title="Thông báo"
        subtitle={unreadCount > 0 ? `${unreadCount} thông báo mới` : 'Tất cả đã đọc'}
        extra={
          unreadCount > 0 ? (
            <Button icon={<Check size={14} />} onClick={() => markAllMutation.mutate()}
              loading={markAllMutation.isPending}>
              Đánh dấu tất cả đã đọc
            </Button>
          ) : undefined
        }
      />

      <Card padding={0}>
        <Tabs
          activeKey={filter}
          onChange={setFilter}
          style={{ padding: '0 20px', borderBottom: '1px solid #eceef1' }}
          items={[
            { key: 'all', label: `Tất cả${notifications.length > 0 ? ` (${notifications.length})` : ''}` },
            { key: 'unread', label: `Chưa đọc${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
            { key: 'TASK_ASSIGNED', label: 'Giao task' },
            { key: 'TASK_UPDATE', label: 'Cập nhật' },
            { key: 'MENTION', label: 'Mention' },
            { key: 'SIGN_REQUEST', label: 'Yêu cầu ký' },
          ]}
        />

        {isLoading ? (
          <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
        ) : notifications.length === 0 ? (
          <Empty description="Không có thông báo" style={{ padding: 40 }} />
        ) : (
          <div>
            {notifications.map((n, i) => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                onMouseEnter={(e) => { e.currentTarget.style.background = !n.isRead ? '#d7f5ec' : '#f6f7f8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = !n.isRead ? '#eefbf8' : '#fff'; }}
                style={{
                  padding: 16,
                  borderBottom: i === notifications.length - 1 ? 0 : '1px solid #eceef1',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  cursor: 'pointer',
                  background: !n.isRead ? '#eefbf8' : '#fff',
                  transition: 'background 0.15s',
                }}
              >
                <Badge dot={!n.isRead} color="#6699CC">
                  <span style={{ fontSize: 22 }}>{TYPE_ICONS[n.type] ?? '📨'}</span>
                </Badge>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: '#5b6875', marginTop: 4, lineHeight: 1.5,
                       overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                       WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
                    {n.content}
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: '#adb6c0', marginTop: 6 }}>
                    {dayjs(n.createdAt).fromNow()} · {dayjs(n.createdAt).format('DD/MM HH:mm')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
